import React from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router';

import {
  Modal,
  ModalActions,
  ModalCancelButton,
  ModalContent,
  ModalHeader,
  ModalSubmitButton,
  showModal,
} from '@edna/components';

import {ModalCenterImage, SecondaryText} from 'src/components/primitives';
import {BlockImage} from 'src/images';

import {
  useChargeStripeAutopaymentMutation,
  useLazyStripeRedirectQuery,
  useOffStripeAutopaymentMutation,
} from '../../api';

type TModalProps = {
  closeModal: TEmptyFunction;
};

const OffAutopaymentModal = React.memo<TModalProps>(({closeModal}) => {
  const {t} = useTranslation();

  const [offAutopayment] = useOffStripeAutopaymentMutation();

  const handleOff = React.useCallback(() => {
    offAutopayment();
    closeModal();
  }, []);

  return (
    <Modal width="430px">
      <ModalHeader>{t('BalanceReplenishment:stripeOffAutopaymentModal.title')}</ModalHeader>
      <ModalContent>
        <SecondaryText>
          {t('BalanceReplenishment:stripeOffAutopaymentModal.subtitle')}
        </SecondaryText>
      </ModalContent>
      <ModalCenterImage as={BlockImage} />
      <ModalActions>
        <ModalCancelButton onClick={closeModal}>{t('Common:button.cancel2')}</ModalCancelButton>
        <ModalSubmitButton onClick={handleOff}>
          {t('BalanceReplenishment:stripeOffAutopaymentModal.submitButton')}
        </ModalSubmitButton>
      </ModalActions>
    </Modal>
  );
});

const AutopaymentSupportModal = React.memo<TModalProps & {isActivate: boolean}>(
  ({closeModal, isActivate}) => {
    const {t} = useTranslation();
    const {id: supportTenantId} = useParams<'id'>();

    const [offAutopayment] = useOffStripeAutopaymentMutation();

    const toggleAutopayment = React.useCallback(() => {
      if (isActivate && supportTenantId) {
        closeModal();

        return;
      }

      offAutopayment(supportTenantId);
      closeModal();
    }, [closeModal, supportTenantId, isActivate]);

    return (
      <Modal width="530px">
        <ModalHeader>
          {t(
            `BalanceReplenishment:stripeAutopaymentSupportModal.title.${isActivate ? 'on' : 'off'}`,
          )}
        </ModalHeader>
        <ModalContent>
          <SecondaryText>
            {t('BalanceReplenishment:stripeAutopaymentSupportModal.subtitle')}
          </SecondaryText>
        </ModalContent>
        <ModalActions>
          <ModalCancelButton onClick={closeModal}>{t('Common:button.cancel2')}</ModalCancelButton>
          <ModalSubmitButton onClick={toggleAutopayment}>
            {t(
              `BalanceReplenishment:stripeAutopaymentSupportModal.submitButton.${isActivate ? 'on' : 'off'}`,
            )}
          </ModalSubmitButton>
        </ModalActions>
      </Modal>
    );
  },
);

const ChangeAutopaymentCreditsModal = React.memo<TModalProps>(({closeModal}) => {
  const {t} = useTranslation();

  const [redirectToStripe] = useLazyStripeRedirectQuery();

  const handleChangeCredits = React.useCallback(async () => {
    try {
      const result = await redirectToStripe().unwrap();

      if (result?.stripeUrl) {
        window.open(result.stripeUrl, '_blank', 'noreferrer');
      }

      closeModal();
    } catch (e) {}
  }, [closeModal]);

  return (
    <Modal width="530px">
      <ModalHeader>{t('BalanceReplenishment:stripeChangeCardCreditsModal.title')}</ModalHeader>
      <ModalContent>
        <SecondaryText>
          {t('BalanceReplenishment:stripeChangeCardCreditsModal.subtitle')}
        </SecondaryText>
      </ModalContent>
      <ModalActions>
        <ModalCancelButton onClick={closeModal}>{t('Common:button.cancel2')}</ModalCancelButton>
        <ModalSubmitButton onClick={handleChangeCredits}>
          {t('Common:button.change')}
        </ModalSubmitButton>
      </ModalActions>
    </Modal>
  );
});

const ChargeAutopaymentModal = React.memo<TModalProps>(({closeModal}) => {
  const {t} = useTranslation();
  const {id: supportTenantId} = useParams<'id'>();

  const [chargeAutopayment] = useChargeStripeAutopaymentMutation();

  const handleChargeAutopayment = React.useCallback(() => {
    if (supportTenantId) {
      chargeAutopayment(supportTenantId);
      closeModal();
    }
  }, [closeModal, supportTenantId]);

  return (
    <Modal width="530px">
      <ModalHeader>{t('BalanceReplenishment:stripeChargeAutopaymentModal.title')}</ModalHeader>
      <ModalContent>
        <SecondaryText>
          {t('BalanceReplenishment:stripeChargeAutopaymentModal.subtitle')}
        </SecondaryText>
      </ModalContent>
      <ModalActions>
        <ModalCancelButton onClick={closeModal}>{t('Common:button.cancel2')}</ModalCancelButton>
        <ModalSubmitButton onClick={handleChargeAutopayment}>
          {t('BalanceReplenishment:stripeChargeAutopaymentModal.submitButton')}
        </ModalSubmitButton>
      </ModalActions>
    </Modal>
  );
});

const showAutopaymentSupportModal = (isActivate: boolean) =>
  showModal((closeModal) => (
    <AutopaymentSupportModal closeModal={closeModal} isActivate={isActivate} />
  ));

const showOffAutopaymentModal = () =>
  showModal((closeModal) => <OffAutopaymentModal closeModal={closeModal} />);

const showChangeAutopaymentCreditsModal = () =>
  showModal((closeModal) => <ChangeAutopaymentCreditsModal closeModal={closeModal} />);

const showChargeAutopaymentModal = () =>
  showModal((closeModal) => <ChargeAutopaymentModal closeModal={closeModal} />);

export {
  showChangeAutopaymentCreditsModal,
  showOffAutopaymentModal,
  showAutopaymentSupportModal,
  showChargeAutopaymentModal,
};
